import type { PlatformDictionary } from '../types';

export const enDictionary: PlatformDictionary = {
  languageName: 'English',
  languageNativeName: 'English',
  layout: {
    defaultMetaTitle: 'Services, Profiles, and Insights',
    defaultMetaDescription:
      'Find trusted professionals, compare services, and read practical insights from the Matcha Events blog.',
    builtWith: 'Built with Next.js, TypeORM, PostgreSQL, and Strapi.',
    headerStatsProfilesValue: 'Profiles',
    headerStatsProfilesTitle: 'Verified Providers',
    headerStatsProfilesDescription:
      'Find specialists by expertise, location, and availability before you reach out.',
    headerStatsServicesValue: 'Services',
    headerStatsServicesTitle: 'Active Listings',
    headerStatsServicesDescription:
      'Compare service scope and pricing models to shortlist the right offer quickly.',
    headerStatsInsightsValue: 'Insights',
    headerStatsInsightsTitle: 'Practical Guides',
    headerStatsInsightsDescription:
      'Read fresh blog posts to plan delivery and avoid common execution mistakes.',
    footerDescription: 'Find providers, services, and practical guidance in one place.',
    footerColumnPlatform: 'Platform',
    footerColumnResources: 'Resources',
    footerColumnLegal: 'Legal',
    footerFaq: 'FAQ',
    footerTerms: 'Terms and Conditions',
    footerPrivacy: 'Privacy Policy',
  },
  localeSwitcher: {
    label: 'Language',
  },
  nav: {
    profiles: 'Profiles',
    services: 'Services',
    admin: 'Admin',
    blog: 'Blog',
    chat: 'Chat',
  },
  auth: {
    openAccountMenu: 'Open account menu',
    openSupport: 'Open support',
    supportTooltip: 'Open the support page',
    accountMenu: 'Account menu',
    signedIn: 'Signed in',
    notSignedIn: 'Not signed in',
    switchAccount: 'Switch account',
    signOut: 'Sign out',
    signIn: 'Sign in',
    signUp: 'Sign up',
    createAccount: 'Create account',
    subtitle: 'Authenticate to access protected platform features.',
    closeDialog: 'Close authentication dialog',
    closeButton: 'Close',
    modeLabel: 'Authentication mode',
    firstName: 'First name',
    lastName: 'Last name',
    identifier: 'Email or phone number',
    identifierPlaceholder: 'name@example.com or +380501234567',
    identifierHint: 'Use your email address or phone in international format.',
    identifierInvalid: 'Enter a valid email address or phone number.',
    password: 'Password',
    passwordHintSignIn: 'Use the password from your account.',
    passwordHintSignUp: 'Use at least 8 characters with one letter and one number.',
    passwordTooShort: 'Password must be at least 8 characters.',
    passwordNeedsLetterAndNumber: 'Password must include at least one letter and one number.',
    submitWaiting: 'Please wait...',
    authFailed: 'Unable to complete authentication.',
    missingToken: 'Authentication succeeded but no access token was returned.',
    appleCredentialMissing: 'Apple sign-in did not return an ID token.',
    appleLoadFailed: 'Unable to load Apple sign-in. Please try again later.',
    continueWithApple: 'Continue with Apple',
    googleCredentialMissing: 'Google sign-in did not return an ID token.',
    googleLoadFailed: 'Unable to load Google sign-in. Please try again later.',
    continueWithGoogle: 'Continue with Google',
  },
  authPage: {
    heroTitle: 'Welcome',
    heroDescription: 'Use these forms to log in or create a new account.',
    signInHeading: 'Sign in',
    signInMissingFields: 'Email/phone number and password are required.',
    signInSubmit: 'Sign in',
    signInSubmitting: 'Signing in...',
    signInFailed: 'Unable to sign in. Please try again.',
    tooManyAttempts: 'Too many attempts. Try again in {minutes} min.',
    signUpHeading: 'Create account',
    signUpMissingFields: 'First name, last name, email/phone number, and password are required.',
    signUpSubmit: 'Sign up',
    signUpSubmitting: 'Signing up...',
    signUpFailed: 'Unable to sign up. Please try again.',
    rememberMe: 'Remember me',
    alreadyHaveAccount: 'Already have an account?',
    noAccountYet: "Don't have an account yet?",
    agreePrefix: 'I agree to the',
    termsAndConditions: 'Terms and Conditions',
    acceptTermsRequired: 'You must accept the terms and conditions.',
    orDivider: 'or',
    cookieNotice:
      'We use cookies to keep you signed in, remember your preferences, and improve the Matcha Events admin experience. Read our',
  },
  common: {
    draft: 'Draft',
    noExcerpt: 'No excerpt available yet.',
    pricingOnRequest: 'Pricing on request',
    from: 'From',
    per: 'Per',
    marketplacePlatform: 'Marketplace Platform',
    marketplaceProfiles: 'Marketplace Profiles',
    marketplaceServices: 'Marketplace Services',
    clearFilter: 'Clear filter',
    backToHome: 'Back to home',
    posted: 'Posted',
    published: 'Published',
    listedBy: 'Listed by',
    noContent: 'No content available.',
    loading: 'Loading...',
    pagination: 'Pagination',
    previous: 'Previous',
    next: 'Next',
    first: 'First',
    last: 'Last',
  },
  adminChrome: {
    homeBreadcrumb: 'Home',
    dashboardBreadcrumb: 'Dashboard',
    eventsBreadcrumb: 'Events',
    messagesBreadcrumb: 'Messages',
    servicesBreadcrumb: 'Services',
    positionsBreadcrumb: 'Positions',
    accountFallback: 'Account',
    profileFallback: 'Profile',
    serviceFallback: 'Service',
    manageProfile: 'Manage profile',
    profilesLabel: 'Profiles',
    profilesLoading: 'Loading profiles...',
    profilesEmpty: 'No profiles available.',
    addProfile: 'Add profile',
    createProfileDialogTitle: 'Add profile',
    createProfileTitlePlaceholder: 'Profile title',
    createProfileDescriptionPlaceholder: 'Description (optional)',
    createProfileValidation: 'Profile title must be at least 2 characters.',
    createProfileAccountMissing: 'Unable to resolve account for profile creation.',
    createProfileSubmit: 'Add profile',
    createProfileSubmitting: 'Creating profile...',
    tableEntriesPerPage: 'entries per page',
    tableSearchPlaceholder: 'Search...',
    tableShowingEntries: 'Showing {start} to {end} of {total} entries',
    footerMadeWith: 'made with',
    footerBy: 'by',
    footerForBetterEvents: 'for better events',
    appDownloadBanner: 'Matcha Events app is available for download',
    appDownloadAppStore: 'App Store',
    appDownloadAppStoreCta: 'Download on the App Store',
    appDownloadGooglePlay: 'Google Play',
    appDownloadGooglePlayCta: 'Get it on Google Play',
    summerPromoDismiss: 'Dismiss banner',
    cancel: 'Cancel',
  },
  createProfilePage: {
    heading: 'Create your profile',
    description: 'Create at least one profile to publish and manage services.',
    titlePlaceholder: 'Profile title',
    descriptionPlaceholder: 'Description (optional)',
    submit: 'Create profile',
    submitting: 'Creating profile...',
    titleValidation: 'Profile title must be at least 2 characters.',
    createFailed: 'Unable to create profile. Please try again.',
  },
  adminInputs: {
    searchCategoriesPlaceholder: 'Search categories',
    searchCategoriesHelper: 'Type a category name and press Enter',
    searchSkillsPlaceholder: 'Search skills',
    searchSkillsHelper: 'Search existing skills or type a new one and press Enter',
    searchExclusionsPlaceholder: 'Add a kind of work',
    searchExclusionsHelper: 'Pick a skill or type your own wording (up to 60 characters) and press Enter',
    relatedSkillsLoading: 'Loading skills for selected categories...',
    relatedSkillsTitle: 'Skills from selected categories',
    searchLanguagesPlaceholder: 'Search languages',
    searchLanguagesHelper: 'Type a language name and press Enter',
    searchCityPlaceholder: 'Search city',
    cityHelperDefault: 'Select a city from the list, or start typing to search.',
    cityHelperNoApi: 'Select a city from the list, or type and press Enter to add it.',
    cityHelperUnavailable:
      'Select a city from the list. Google autocomplete is currently unavailable.',
    googleFallbackDisabled:
      'Google autocomplete is disabled. Set REACT_APP_GOOGLE_MAPS_API_KEY to enable suggestions.',
    googleFallbackUnavailable:
      'Google autocomplete is currently unavailable. You can still type manually.',
  },
  adminProfileEditor: {
    unavailable: 'Profile details are unavailable.',
    slugTaken: 'This profile slug is already taken. Choose another one.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'First name',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'Last name',
    titleLabel: 'Title',
    titlePlaceholder: 'Profile title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Profile description',
    categoriesLabel: 'Categories',
    skillsLabel: 'Skills',
    exclusionsLabel: 'Not available for',
    exclusionInvalid: 'An item must be up to 60 characters, with no links, mentions or phone numbers.',
    exclusionSkillConflict: 'A skill cannot be both among the skills and in “Not available for”. Remove it from one of the lists.',
    slugLabel: 'Profile slug',
    slugTooltip: 'Public URL: /profiles/{slug}',
    slugPlaceholder: 'your-profile-slug',
    citiesLabel: 'Cities',
    coverageRadiusLabel: 'Coverage radius (km)',
    coverageRadiusPlaceholder: '50',
    languagesLabel: 'Languages',
    contactsLabel: 'Contacts',
    addContact: 'Add contact',
    noContacts: 'No contacts yet. Add one or more profile contacts.',
    contactEmailPlaceholder: 'name@example.com',
    contactPhonePlaceholder: '+1 (555) 123-4567',
    contactDetailsPlaceholder: 'Contact details',
    socialLinksLabel: 'Social links',
    addLink: 'Add link',
    noLinks: 'No links yet. Add one or more profile social links.',
    socialLinkPlaceholder: 'https://social-link.example',
    uploadAvatar: 'Upload avatar',
    uploadingAvatar: 'Uploading...',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    deleteAction: 'Delete',
    avatarUploadSizeLimit: 'File must be 50 MB or smaller.',
    avatarUploaded: 'Avatar uploaded.',
    avatarFileType: 'Avatar file must be an image.',
    avatarMismatch: 'Uploaded avatar does not match the active profile.',
    avatarUploadFailed: 'Unable to upload avatar. Please try again.',
    contactMissingValue: 'Each contact row must include a value.',
    contactUniqueType: 'Use each contact type only once.',
    socialMissingUrl: 'Each social link row must include a URL.',
    socialUniquePlatform: 'Use each social platform only once.',
    cityMissingValue: 'Each city row must include a city value.',
    cityPlaceIdMissing: 'Select each city from suggestions so it includes a valid place ID.',
    cityDuplicate: 'Duplicate cities are not allowed.',
    titleValidation: 'Profile title must be at least 2 characters.',
    coverageRadiusValidation: 'Coverage radius must be an integer between 0 and 1000 km.',
    slugMin: 'Profile slug must be at least 3 characters.',
    slugMax: 'Profile slug must be at most 128 characters.',
    profileUpdated: 'Profile updated.',
    profileUuidMissing: 'Profile UUID is missing.',
    profileNotFound: 'Profile was not found.',
    profileLoadFailed: 'Unable to load profile details.',
    profileUpdateFailed: 'Unable to update profile. Please try again.',
    loadingProfile: 'Loading profile...',
    profileCardFallbackTitle: 'Profile',
    profileCardFallbackSubtitle: 'Service profile',
    profileAvatarAlt: 'Profile avatar',
  },
  adminServiceEditor: {
    createTitle: 'Create service',
    updateTitle: 'Update service',
    updateDescription: 'Edit service details on a dedicated page.',
    loading: 'Loading service...',
    notFound: 'Service not found.',
    loadFailed: 'Unable to load service. Please try again.',
    titleLabel: 'Title',
    titlePlaceholder: 'Service title',
    slugLabel: 'Service slug',
    slugTooltip:
      'Slug is the public URL part for this service (the last segment in /profiles/{profileSlug}/services/{serviceSlug}). Use lowercase letters, numbers, and dashes.',
    slugPlaceholder: 'Service slug (optional)',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Full description for service page (800-1200 words)',
    shortDescriptionLabel: 'Short description',
    shortDescriptionPlaceholder: 'Short description for service card (20-40 words)',
    seoDescriptionLabel: 'SEO description',
    seoDescriptionPlaceholder: 'SEO description (150-160 characters)',
    pricingModeLabel: 'Pricing mode',
    currencyLabel: 'Currency',
    pricingModeFixed: 'Fixed',
    pricingModeFrom: 'From',
    pricingModeRange: 'Range (min/max)',
    priceLabel: 'Price',
    fromPriceLabel: 'From price',
    minimumPriceLabel: 'Minimum price',
    pricePlaceholder: 'Price',
    fromPricePlaceholder: 'Starting from',
    minimumPricePlaceholder: 'Minimum price',
    maximumPriceLabel: 'Maximum price',
    maximumPricePlaceholder: 'Maximum price',
    unitLabel: 'Unit (optional)',
    unitNone: 'None',
    unitHour: 'Per hour',
    unitDay: 'Per day',
    unitWeek: 'Per week',
    unitMonth: 'Per month',
    unitSession: 'Per session',
    unitItem: 'Per item',
    unitCustom: 'Custom',
    customUnitPlaceholder: 'Custom unit (e.g., per m2)',
    mediaLabel: 'Media',
    addMediaEmpty: 'Add media to this service.',
    addMediaAction: 'Add media',
    rehostFromLink: 'Rehost from link',
    removeMediaAria: 'Remove media',
    altTextPlaceholder: 'Alt text',
    altTextAria: 'Alt text for {fileName}',
    addMediaDialogTitle: 'Add media',
    mediaSourceLabel: 'Source',
    mediaSourceFiles: 'Upload files',
    mediaSourceUrl: 'Rehost from link',
    mediaUrlPlaceholder: 'https://youtube.com/watch?v=...',
    mediaUrlHelper: 'Paste a media URL to download and rehost it in your storage.',
    mediaTypeUrlLabel: 'Media type (optional)',
    mediaTypeFallbackLabel: 'Media type for undetected files',
    mediaTypePlaceholder: 'Select media type',
    mediaTypeImage: 'Image',
    mediaTypeVideo: 'Video',
    mediaTypeMusic: 'Music',
    mediaTypeFile: 'File',
    selectedUrlTemplate: 'Selected URL: {value}',
    selectedUrlEmpty: 'Provide a media URL to rehost.',
    selectedFileTemplate: 'Selected file: {value}',
    selectedFilesTemplate: 'Selected files: {count}',
    detectedMediaTypeTemplate: 'Detected media type: {types}.',
    detectedMediaTypesTemplate: 'Detected media types: {types}.',
    undetectedMediaFileTemplate: 'Could not detect media type for 1 file.',
    undetectedMediaFilesTemplate: 'Could not detect media type for {count} files.',
    addMediaSubmit: 'Add media',
    addMediaSubmitting: 'Adding...',
    rehostMediaSubmit: 'Rehost media',
    rehostMediaSubmitting: 'Rehosting...',
    mediaDeleteDialogTitle: 'Delete media',
    mediaDeleteDialogDescription:
      'Are you sure you want to delete {title}? This action cannot be undone.',
    mediaDeleteDialogFallbackTitle: 'this media item',
    mediaDeleteDialogConfirm: 'Delete',
    mediaDeleteInProgress: 'Deleting...',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    createSubmit: 'Create',
    createSubmitting: 'Creating...',
    titleValidation: 'Service title must be at least 2 characters.',
    slugMin: 'Service slug must be at least 3 characters.',
    slugMax: 'Service slug must be at most 128 characters.',
    shortDescriptionMax: 'Short description must be at most 280 characters.',
    seoDescriptionMax: 'SEO description must be at most 320 characters.',
    priceValidation: '{label} must be a valid number greater than or equal to 0.',
    maximumPriceValidation: 'Maximum price must be a valid number greater than or equal to 0.',
    maximumPriceRangeValidation: 'Maximum price must be greater than or equal to minimum price.',
    customUnitRequired: 'Custom unit is required when unit type is custom.',
    customUnitInvalid: 'Custom unit can be set only when unit type is custom.',
    createFailed: 'Unable to create service. Please try again.',
    updateFailed: 'Unable to update service. Please try again.',
    slugTaken: 'This service slug is already taken. Choose another one.',
    mediaUploadSizeLimit: 'Each uploaded file must be 1 GB or smaller.',
    serviceIdentifierMissing: 'Service identifier is missing.',
    mediaUrlRequired: 'Media URL is required.',
    mediaFilesRequired: 'At least one file is required.',
    mediaTypeRequired: 'Media type is required when automatic detection fails.',
    deleteAction: 'Delete service',
    deleteDialogTitle: 'Delete service',
    deleteDialogDescription:
      'Are you sure you want to delete {title}? This action cannot be undone.',
    deleteDialogFallbackTitle: 'this service',
    deleteDialogCancel: 'Cancel',
    deleteDialogConfirm: 'Delete',
    deleteInProgress: 'Deleting...',
    deleteFailed: 'Unable to delete service. Please try again.',
    citiesLabel: 'Cities',
    citiesSameAsProfile: 'Same as profile',
    citiesProfileEmpty: 'The profile has no cities yet.',
    citiesRequired: 'Add at least one city or switch "Same as profile" on.',
    citiesResetDialogTitle: 'Use the profile cities?',
    citiesResetDialogDescription: "The service's own cities will be removed.",
    citiesResetDialogConfirm: 'Remove cities',
  },
  home: {
    title: 'Home',
    description:
      'Discover specialized service providers, compare service offers, and explore the latest Matcha Events platform updates.',
    heroTitle: 'Find the event team you’ll love.',
    heroDescription:
      'Browse event organizers and specialists, compare services, and book with confidence.',
    ctaBrowseProfiles: 'Browse profiles',
    ctaBrowseServices: 'Browse services',
    ctaReadBlog: 'Read the blog',
    featuredProfiles: 'Featured profiles',
    viewAll: 'View all',
    noProfiles:
      'No profiles are available yet. Add data in your PostgreSQL `profiles` and `services` tables to populate this section.',
    servicesCount: 'services',
    fromBlog: 'From the blog',
    seeAllPosts: 'See all posts',
    noPostsConfigured:
      'Blog posts will appear here after `STRAPI_BASE_URL` is configured and Strapi has published entries.',
    noPostsYet:
      'No posts were returned from Strapi yet. Publish content in your configured collection to populate this section.',
    discoverNaturalLabel: 'Describe what you need',
    discoverFormTitle: 'Who are you looking for?',
    discoverNaturalPlaceholder:
      'Need a wedding photographer in your area under $500 with event shooting experience.',
    discoverNaturalExamples: [
      'Need a wedding photographer in your area under $500 with event shooting experience.',
      'Looking for a floral designer for a 200-person gala.',
      'Find a sound engineer for a 2-day conference.',
    ],
    discoverNaturalSubmit: 'Find matches',
    discoverFiltersLabel: 'Refine filters',
    discoverQueryLabel: 'Search query',
    discoverLocationLabel: 'Location',
    discoverBudgetMinLabel: 'Budget min',
    discoverBudgetMaxLabel: 'Budget max',
    discoverCurrencyLabel: 'Currency',
    discoverCurrencyAny: 'Any currency',
    discoverCategoriesLabel: 'Categories',
    discoverCategoryAny: 'Any category',
    discoverCategoryPlaceholder: 'Search and add categories',
    discoverSkillsLabel: 'Skills',
    discoverSkillPlaceholder: 'Search and add skills',
    discoverAddSkill: 'Add',
    discoverApplyFilters: 'Apply filters',
    discoverSuggestedSkills: 'Suggested skills',
    discoverProfilesResults: 'Matching profiles',
    discoverServicesResults: 'Matching services',
    discoverNoResults: 'No matches found for the current filters.',
    discoverSearching: 'Searching...',
    discoverSkillLoading: 'Loading skills...',
    discoverSearchFailed: 'Unable to run search. Please try again.',
  },
  servicesPage: {
    navLabel: 'My services',
    title: 'Services',
    description: 'Browse active service listings with transparent pricing and provider context.',
    heroTitle: 'Browse active services',
    heroDescription:
      'Each service combines pricing structure from the service entity with profile context from the related provider, using card blocks from the current platform design.',
    noServices:
      'No active services were returned. Make sure the `services` table has rows with `deleted_at IS NULL`.',
    priceModeFrom: 'From',
    priceModeRange: 'Range',
    priceModeFixed: 'Fixed',
    viewDetails: 'View details',
    adminListDescription: 'Services created in your active profile.',
    adminCreateAction: 'Create service',
    adminSearchPlaceholder: 'Search services',
    adminCountSummary: '{visible} of {total} services',
    adminLoading: 'Loading services...',
    adminEmpty: 'No services found yet.',
    adminEmptySearch: 'No services match your search.',
    adminAccountMissing: 'Account not found. Sign in again.',
    adminProfileMissing: 'No profiles found. Create a profile first.',
    adminEditAction: 'Edit service',
    adminSlugPending: 'Slug will be generated',
    adminLoadFailed: 'Unable to load services. Please try again.',
  },
  eventsPage: {
    navLabel: 'Events',
    title: 'Events',
    listDescription: 'Events created in your account with start and end times.',
    createAction: 'Create event',
    accountMissing: 'Account not found. Sign in again.',
    loadFailed: 'Unable to load events. Please try again.',
    loading: 'Loading events...',
    empty: 'No events found yet.',
    createDialogTitle: 'Create event',
    createSubmit: 'Create',
    createSubmitting: 'Creating...',
    createFailed: 'Unable to create event. Please try again.',
    notFound: 'Event not found.',
    updateTitle: 'Update event',
    updateDescription: 'Edit event details on a dedicated page.',
    updateFailed: 'Unable to update event. Please try again.',
    loadingSingle: 'Loading event...',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    titlePlaceholder: 'Event title',
    descriptionPlaceholder: 'Description (optional)',
    cityPlaceholder: 'City (optional)',
    cityHelper: 'Start typing a city and select from Google suggestions.',
    locationPlaceholder: 'Location / venue (optional)',
    locationHelper: 'Search for venue or address suggestions in the selected area.',
    selectCityFirst: 'Select a city first to set location details.',
    tableEvent: 'event',
    tableCityLocation: 'city / location',
    tableStarts: 'starts',
    tableEnds: 'ends',
    tableDescription: 'description',
    cityLabel: 'City',
    locationLabel: 'Location',
    notSpecified: 'Not specified',
    noDescription: 'No description',
    unknownDate: 'Unknown',
    positionsTitle: 'Event positions',
    positionsDescription: 'Create staffing positions tied to this event.',
    requestPosition: 'Request position',
    positionsLoading: 'Loading positions...',
    positionsLoadFailed: 'Unable to load positions for this event. Please try again.',
    positionsEmpty: 'No positions linked to this event yet.',
    validationAccountMissing: 'No account found. Sign in again and try once more.',
    validationTitleMin: 'Event title must be at least 2 characters.',
    validationDescriptionMax: 'Description must be less than or equal to 2000 characters.',
    validationCityMax: 'City must be less than or equal to 512 characters.',
    validationLocationMax: 'Location must be less than or equal to 255 characters.',
    validationStartInvalid: 'Start time must be a valid date and time.',
    validationEndInvalid: 'End time must be a valid date and time.',
    validationEndAfterStart: 'End time must be after start time.',
  },
  positionsPage: {
    navLabel: 'Positions',
    editTitle: 'Update position',
    editDescription: 'Edit position details and manage publishing status.',
    loading: 'Loading position...',
    notFound: 'Position not found.',
    loadFailed: 'Unable to load position. Please try again.',
    updateFailed: 'Unable to update position. Please try again.',
    statusLabel: 'Status',
    titlePlaceholder: 'Position title',
    descriptionPlaceholder: 'Description (optional)',
    locationPlaceholder: 'Location (optional)',
    budgetCurrencyLabel: 'Budget currency',
    budgetMinPlaceholder: 'Minimum budget (optional)',
    budgetMaxPlaceholder: 'Maximum budget (optional)',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    publish: 'Publish',
    publishing: 'Publishing...',
    publishedSuccess: 'Position published.',
    publishFailed: 'Unable to publish position. Please try again.',
    close: 'Close',
    closing: 'Closing...',
    closeFailed: 'Unable to close position. Please try again.',
    delete: 'Delete',
    deleting: 'Deleting...',
    deleteConfirm: 'Delete this position? This cannot be undone.',
    deleteFailed: 'Unable to delete position. Please try again.',
    requestDialogTitle: 'Request position',
    requestSubmit: 'Request',
    requestSubmitting: 'Creating...',
    requestFailed: 'Unable to create position. Please try again.',
    tablePosition: 'position',
    tableStatus: 'status',
    tableBudget: 'budget',
    tableSchedule: 'schedule',
    tableSkills: 'skills',
    noBudget: 'No budget',
    budgetFrom: 'From {value}',
    budgetUpTo: 'Up to {value}',
    flexibleSchedule: 'Flexible',
    noSkills: 'No skills',
    validationEventMissing: 'Event not found. Reload the page and try again.',
    validationAccountMissing: 'No account found. Sign in again and try once more.',
    validationTitleMin: 'Position title must be at least 2 characters.',
    validationDescriptionMax: 'Description must be less than or equal to 2000 characters.',
    validationLocationMax: 'Location must be less than or equal to 128 characters.',
    validationBudgetMin: 'Minimum budget must be a number greater than or equal to 0.',
    validationBudgetMax: 'Maximum budget must be a number greater than or equal to 0.',
    validationBudgetMinRequired: 'Minimum budget is required when maximum budget is provided.',
    validationBudgetRange: 'Maximum budget must be greater than or equal to minimum budget.',
    validationScheduleTogether: 'Start and end times must be provided together.',
    validationStartInvalid: 'Start time must be a valid date and time.',
    validationEndInvalid: 'End time must be a valid date and time.',
    validationEndAfterStart: 'End time must be after start time.',
  },
  proposalsPage: {
    title: 'Proposals',
    navLabel: 'My proposals',
    listDescription: 'Track active and archived proposals for your active profile.',
    activeLabel: 'Active',
    archivedLabel: 'Archived',
    invitesFilterLabel: 'Invites ({count})',
    discussingFilterLabel: 'Discussing ({count})',
    submittedFilterLabel: 'Submitted ({count})',
    loading: 'Loading proposals...',
    loadFailed: 'Unable to load proposals. Please try again.',
    empty: 'No proposals yet.',
    accountMissing: 'Account not found. Sign in again.',
    profileMissing: 'No active profile found. Select or create a profile first.',
    serviceSubtitle: 'Proposal sent with {serviceTitle}',
    openPositionAction: 'Open position',
    acceptAction: 'Accept',
    declineAction: 'Decline',
    proposedPriceLabel: 'Proposed price',
    acceptFailed: 'Unable to accept the proposal. Please try again.',
    declineFailed: 'Unable to decline the proposal. Please try again.',
    statusApproved: 'Approved',
    statusCanceled: 'Canceled',
    statusDeclined: 'Declined',
    statusDiscussing: 'Discussing',
    statusRejected: 'Rejected',
    statusSubmitted: 'Submitted',
    statusViewed: 'Viewed',
  },
  analyticsPage: {
    navLabel: 'Dashboard',
    title: 'Analytics',
    description:
      'Review provider traffic, matched discovery activity, and engagement for your active profile.',
    range7d: '7d',
    range30d: '30d',
    range90d: '90d',
    loading: 'Loading analytics...',
    loadFailed: 'Unable to load analytics. Please try again.',
    profileMissing: 'No active profile found. Select or create a profile first.',
    noData: 'No analytics data is available for this period yet.',
    uniqueVisitors: 'Unique visitors',
    sessions: 'Sessions',
    profileViews: 'Profile views',
    profileIncomplete: 'Profile not completed',
    contactsIncomplete: 'Contacts not completed',
    socialLinksIncomplete: 'Social links not completed',
    servicesIncomplete: 'Services not completed',
    servicesListViews: 'Services list views',
    serviceViews: 'Service views',
    contactClicks: 'Contact clicks',
    socialClicks: 'Social clicks',
    matchedProfileImpressions: 'Matched profile impressions',
    matchedServiceImpressions: 'Matched service impressions',
    matchedProfileClicks: 'Matched profile clicks',
    matchedServiceClicks: 'Matched service clicks',
    totalViewsChartTitle: 'Traffic trend',
    totalViewsChartDescription: 'Combined profile and service views for the selected period.',
    topServicesTitle: 'Top services',
    topCitiesTitle: 'Top visitor cities',
    serviceColumn: 'Service',
    cityColumn: 'City',
    viewsColumn: 'Views',
    visitorsColumn: 'Visitors',
    sessionsColumn: 'Sessions',
    matchedClicksColumn: 'Matched clicks',
  },
  profilesPage: {
    title: 'Profiles',
    description:
      'Browse provider profiles, budgets, and active service offerings on Matcha Events.',
    heading: 'Profiles',
    filteredByLocation: 'Filtered by location:',
    noProfilesForLocation: 'No profiles matched this location filter.',
    noProfiles:
      'No profile rows were returned. Confirm your `TYPEORM_*` variables and make sure profiles and services contain data in the selected schema.',
    activeServices: 'active services',
  },
  blogPage: {
    title: 'Blog',
    description: 'Read platform news, guides, and practical insights from Matcha Events.',
    heading: 'Blog',
    missingStrapi:
      'Set `STRAPI_BASE_URL` in `apps/platform/.env.local` to load blog content from Strapi.',
    noPosts:
      'No posts were returned from Strapi yet. Publish content in your configured collection to populate this page.',
  },
  blogPostPage: {
    missingTitle: 'Article not found',
    badge: 'Blog article',
  },
  faqPage: {
    title: 'FAQ',
    description:
      'Find answers to common questions about profiles, services, and platform workflows.',
    heading: 'Frequently Asked Questions',
    missingStrapi:
      'Set `STRAPI_BASE_URL` in `apps/platform/.env.local` to load FAQ content from Strapi.',
    noItems:
      'No FAQ items were returned from Strapi yet. Publish entries in your FAQ collection to populate this page.',
    noAnswer: 'No answer is available yet.',
  },
  aiPage: {
    title: 'Official ChatGPT Assistant',
    description:
      'Use the official Matcha Events ChatGPT assistant for authenticated request submission and account-specific actions.',
    badge: 'Official Assistant',
    heading: 'Use our official ChatGPT assistant',
    paragraph:
      'For authenticated request submission from chat, use the official Matcha Events ChatGPT assistant. This is the primary assistant flow for account-specific actions.',
    openAssistant: 'Open official ChatGPT assistant',
    missingAssistantUrl: '`NEXT_PUBLIC_OFFICIAL_GPT_URL` is not configured yet.',
    quickStart: 'Quick start',
    step1: 'Open the official assistant link above.',
    step2: 'Sign in when prompted.',
    step3: 'Submit your request directly in chat.',
    copyPromptLabel: 'Copy/paste prompt:',
    copyPrompt: 'Open the official Matcha Events assistant for authenticated request submission.',
  },
  legalPage: {
    privacyTitle: 'Privacy Policy',
    privacyDescription: 'Review how Matcha Events collects, processes, and protects personal data.',
    termsTitle: 'Terms and Conditions',
    termsDescription: 'Read the terms that govern use of the Matcha Events platform and services.',
    missingStrapi:
      'Set `STRAPI_BASE_URL` in `apps/platform/.env.local` to load legal pages from Strapi.',
    missingPrivacy:
      'No published Privacy Policy page was found in Strapi. Create a `legal-page` entry with slug `privacy-policy`.',
    missingTerms:
      'No published Terms and Conditions page was found in Strapi. Create a `legal-page` entry with slug `terms-and-conditions`.',
  },
  notFoundPage: {
    heading: 'Page not found',
    description: 'The resource you requested does not exist or is no longer available.',
    backToHome: 'Back to home',
  },
  messagesPage: {
    navLabel: 'Messages',
    heading: 'Messages',
    subtitle: 'Conversations in your account. Open any thread and send replies.',
    searchPlaceholder: 'Search conversations',
    newChatAction: 'New chat',
    startChatTitle: 'Start chat',
    startChatDescription: 'Enter the exact username of the account you want to message.',
    usernameLabel: 'Username',
    usernamePlaceholder: 'username',
    usernameRequired: 'Enter a username to start a chat.',
    startChatConfirm: 'Start chat',
    startChatSubmitting: 'Starting...',
    emptyTitle: 'No conversations yet',
    emptyDescription: 'Start a direct chat by entering an exact username.',
    emptyConversation: 'Select a conversation to view messages.',
    noMessages: 'No messages yet.',
    notFound: 'Conversation not found.',
    accountMissing: 'Account not found. Sign in again.',
    loadFailed: 'Unable to load messages. Please try again.',
    startChatFailed: 'Unable to start chat. Please try again.',
    sendFailed: 'Unable to send message. Please try again.',
    loadingConversations: 'Loading conversations...',
    loadingMessages: 'Loading messages...',
    messagePlaceholder: 'Write a message...',
    sendAction: 'Send',
    sending: 'Sending...',
    directConversationSubtitle: '{name}',
    positionConversationSubtitle: 'Position chat with {name}',
    eventConversationSubtitle: 'Event chat with {name}',
    unknownParticipant: 'Unknown user',
    typeDirect: 'Direct',
    typePosition: 'Position',
    typeEvent: 'Event',
    createdAtLabel: 'Updated {date}',
  },
  galleriesPage: {
    navLabel: 'Galleries',
    title: 'Galleries',
    loading: 'Loading galleries...',
    loadFailed: 'Could not load the galleries. Please try again.',
    notFound: 'This gallery was not found. It may have been deleted.',
    profileMissing: 'No profile found. Create a profile first.',
    newGallery: '+ New gallery',
    creating: 'Creating...',
    untitled: 'Untitled gallery',
    noDate: 'No date',
    fileCount: {
      one: '{count} file',
      few: '{count} files',
      many: '{count} files',
      other: '{count} files',
    },
    status: {
      ACTIVE: 'Active',
      SELECTION_SENT: 'Selection sent',
      EXPIRED: 'Expired',
    },
    banner: {
      text: {
        one: '♥ “{title}”: guests sent their selection — {count} file',
        few: '♥ “{title}”: guests sent their selection — {count} files',
        many: '♥ “{title}”: guests sent their selection — {count} files',
        other: '♥ “{title}”: guests sent their selection — {count} files',
      },
      view: 'View',
      close: 'Close',
    },
    counters: {
      views: 'Views',
      downloads: 'Files downloaded',
      zips: 'ZIP archives',
      favorites: 'Favorites',
      selected: 'Selected',
    },
    settingsButton: 'Settings',
    share: {
      title: 'Share',
      linkLabel: 'Gallery link',
      noLink: 'The link appears once your profile has a site address.',
      copyForClient: 'Copy for client',
      copied: 'Copied: paste it into a message to the client',
      copyFailed: 'Could not copy. Select the text and copy it by hand.',
      openAsGuest: 'Open as guest',
      reissue: 'Reissue link',
      reissueTitle: 'Reissue the link?',
      reissueBody: 'The old link stops working at once. The selection, comments and counters stay. Send the new link to the client.',
      reissueConfirm: 'Reissue',
      reissued: 'The new link is ready',
      password: 'Password: {password}',
      passwordHint: 'Send it with the link or separately.',
      noPassword: 'No password: anyone with the link can open the gallery.',
      newPassword: 'New password',
      newPasswordDone: 'New password set: send it to the client',
      linkTemplate: '{title}: {link}',
      passwordTemplate: 'Password: {password}',
    },
    files: {
      allFiles: 'All files',
      selectionTab: '♥ Selection ({count})',
      sectionUnnamed: 'Untitled section',
      sectionsLabel: 'Sections',
      addSectionPlaceholder: 'New section',
      addSection: '+ Section',
      renameSection: 'Rename',
      renameSectionTitle: 'Rename section',
      sectionNameLabel: 'Section name',
      save: 'Save',
      deleteSection: 'Delete section',
      deleteSectionTitle: 'Delete “{name}”?',
      deleteSectionBody: {
        one: 'The section and its {count} file are deleted for good.',
        few: 'The section and its {count} files are deleted for good.',
        many: 'The section and its {count} files are deleted for good.',
        other: 'The section and its {count} files are deleted for good.',
      },
      empty: 'No files yet. Drop photos and videos above.',
      emptySection: 'No files in this section yet.',
      dropTitle: 'Drag photos and videos here',
      dropHint: 'Or click to choose. Hundreds of files at once: photos up to 50 MB, videos up to 1 GB and 10 minutes (MP4 or MOV, H.264).',
      uploading: 'Uploading {done} of {total}',
      uploaded: 'Uploaded {done} of {total}',
      notAccepted: {
        one: '{count} file not accepted',
        few: '{count} files not accepted',
        many: '{count} files not accepted',
        other: '{count} files not accepted',
      },
      clearUploads: 'Clear',
      processing: 'Processing...',
      processingFailed: 'Preview not made',
      video: 'Video',
      cover: 'Cover',
      makeCover: 'Make cover',
      moveTo: 'Move to “{name}”',
      deleteFile: 'Delete',
      deleteFileTitle: 'Delete this file?',
      deleteFileBody: '“{name}” is deleted for good, with its selection mark and comments.',
      fileActions: 'File actions',
      hidden: 'Hidden by guest',
      restore: 'Show to guests again',
      restored: 'Guests see it again',
      dragHint: 'Drag files to reorder them within a section.',
    },
    uploadErrors: {
      FILE_TYPE: 'Not a photo or an MP4/MOV video',
      PHOTO_TOO_LARGE: 'Photo over 50 MB',
      VIDEO_TOO_LARGE: 'Video over 1 GB',
      GALLERY_VIDEO_TOO_LONG: 'Video longer than 10 minutes',
      GALLERY_VIDEO_NOT_H264: 'Video is not H.264 (on iPhone, choose “Most Compatible”)',
      GALLERY_VIDEO_UNREADABLE: 'Could not read the video',
      STORAGE_LIMIT_EXCEEDED: 'Storage is full',
      FAILED: 'Upload failed',
    },
    selection: {
      heading: 'Guests’ selection: {count}',
      headingWithLimit: 'Guests’ selection: {count} / {limit}',
      sentAt: 'Sent {date}',
      notSent: 'Not sent yet: guests are choosing',
      copyNames: 'Copy file names',
      namesCopied: 'File names copied: paste them into a Lightroom search',
      unlock: 'Unlock selection',
      unlocked: 'Selection unlocked: guests can change it again',
      empty: 'Guests have not picked anything yet.',
      anonymous: 'no name',
    },
    settings: {
      title: 'Settings',
      titleLabel: 'Title',
      titlePlaceholder: 'For example, Olena & Max',
      shootDateLabel: 'Shoot date',
      styles: {
        COVER: 'Cover',
        EDITORIAL: 'Editorial',
        COMPACT: 'Compact',
      },
      layouts: {
        GRID: 'Grid',
        MASONRY: 'Masonry',
        SLIDESHOW: 'Slideshow',
      },
      access: 'Access',
      passwordLabel: 'Password',
      passwordPlaceholder: 'No password',
      passwordInvalid: 'The password must be at least 4 characters.',
      expiresLabel: 'Access until (optional)',
      expiresHint: 'After this date the gallery is hidden; the files stay.',
      expiresClear: 'Remove date',
      expiryLockedHint: 'Included from the Pro plan. A date or deletion already set keeps working and can be removed. Pro is bought in the Matcha app, under My subscription.',
      autoDeleteLabel: 'Delete files after expiry',
      autoDelete: {
        OFF: 'Don’t delete',
        NOW: 'Right after expiry',
        AFTER_30_DAYS: 'After 30 days',
        AFTER_90_DAYS: 'After 90 days',
      },
      autoDeleteHint: 'For good. A push comes 7 days before, so you can cancel or extend.',
      autoDeleteNeedsExpiry: 'Set an access date first.',
      guests: 'What guests can do',
      downloadsLabel: 'Downloads',
      downloads: {
        ALL: 'Originals and web size',
        WEB: 'Web size only (2048 px)',
        ORIGINAL: 'Originals only',
        OFF: 'Off',
      },
      languageLabel: 'Gallery language',
      languages: {
        SITE: 'As the site',
        uk: 'Українська',
        en: 'English',
        ru: 'Русский',
      },
      languageHint: 'Buttons, hints and the password screen for guests. Your titles and captions stay as written.',
      guestHintsLabel: 'Hints for guests',
      guestHintsSwitch: 'Show guests the hints',
      guestHintsHint: 'Short steps shown once on each device after the password: how to pick, send and download.',
      allowHidingLabel: 'Hidden photos',
      allowHidingSwitch: 'Let guests hide photos',
      allowHidingHint: 'A guest can hide a photo or video for everyone with the link. You still see it here, marked, and can bring it back.',
      watermarkLabel: 'Watermark',
      watermarkSwitch: 'Show my watermark to guests',
      watermarkHint: 'Guests see and download web-size photos with your watermark. Videos and the cover stay clean.',
      watermarkOriginalsHint: 'Originals download without the watermark.',
      watermarkEdit: 'Watermark design',
      selectionLimitLabel: 'Selection limit (empty for none)',
      selectionLimitInvalid: 'The selection limit must be a whole number from 1.',
      done: 'Done',
      saving: 'Saving...',
      saved: 'Settings saved',
      deleteGallery: 'Delete gallery',
      deleteTitle: 'Delete “{title}”?',
      deleteBody: {
        one: 'Its {count} file, the selection and comments are deleted for good. The link stops working.',
        few: 'All {count} files, the selection and comments are deleted for good. The link stops working.',
        many: 'All {count} files, the selection and comments are deleted for good. The link stops working.',
        other: 'All {count} files, the selection and comments are deleted for good. The link stops working.',
      },
      deleteConfirm: 'Delete',
      deleted: 'Gallery deleted',
    },
    errors: {
      actionFailed: 'Could not save the change. Please try again.',
      GALLERY_PROVIDER_ONLY: 'Only a Provider profile can have galleries.',
      GALLERY_LIMIT_REACHED: 'Your plan has room for no more galleries. Delete one you no longer need; the Pro plan has no limit.',
      GALLERY_TITLE_TOO_LONG: 'The title is too long.',
      GALLERY_SHOOT_DATE_INVALID: 'The shoot date is not valid.',
      GALLERY_SELECTION_LIMIT_INVALID: 'The selection limit must be a whole number from 1.',
      GALLERY_LAST_SECTION: 'A gallery keeps at least one section.',
      GALLERY_COVER_NOT_PHOTO: 'The cover must be a photo.',
      GALLERY_PLAN_REQUIRED: 'This setting is included in the Pro plan. Pro is bought in the Matcha app, under My subscription.',
      GALLERY_WATERMARK_LOGO_REQUIRED: 'Upload a PNG logo before choosing it as the watermark.',
      GALLERY_WATERMARK_TEXT_TOO_LONG: 'The watermark text is too long.',
    },
    watermark: {
      title: 'Watermark',
      locked: 'The watermark is included in the Pro plan. You can look at it here and change it once you have Pro; Pro is bought in the Matcha app.',
      loadFailed: 'Could not load the watermark.',
      preview: 'Watermark preview',
      previewHint: 'Approximate. The real watermark is sized to each photo.',
      kindLabel: 'Mark',
      kinds: {
        TEXT: 'Text',
        LOGO: 'Logo',
      },
      textLabel: 'Text',
      textPlaceholder: 'Your name or studio',
      textHint: 'Leave it empty to use your profile name.',
      logoLabel: 'Logo',
      logoHint: 'A PNG file up to 5 MB. A transparent background looks best.',
      storedLogoHint: 'Your logo is kept but not used. Remove it to free up storage.',
      noLogo: 'No logo yet',
      chooseLogo: 'Choose a PNG',
      replaceLogo: 'Replace logo',
      removeLogo: 'Remove logo',
      removeLogoTitle: 'Remove the logo?',
      removeLogoBody: 'The file is deleted and the watermark becomes text again.',
      removeLogoConfirm: 'Remove',
      uploading: 'Uploading...',
      logoUploaded: 'Logo added',
      logoRemoved: 'Logo removed',
      logoErrors: {
        notPng: 'The logo must be a PNG file.',
        tooLarge: 'The logo can be up to 5 MB.',
        STORAGE_LIMIT_EXCEEDED: 'There is not enough space left in your storage.',
        FAILED: 'Could not upload the logo. Please try again.',
      },
      positionLabel: 'Position',
      positions: {
        CENTER: 'Center',
        BOTTOM_RIGHT: 'Corner',
        TILED: 'Tiled',
      },
      strengthLabel: 'Strength',
      strengths: {
        LIGHT: 'Light',
        MEDIUM: 'Medium',
        STRONG: 'Strong',
      },
      appliesHint: 'Shown on web-size photos in galleries with the Watermark switched on. Originals, videos and the cover stay clean.',
      saved: 'Watermark saved',
      done: 'Done',
      saving: 'Saving...',
    },
    design: {
      open: 'Design',
      back: 'Files',
      title: 'Design',
      intro: 'Every change is saved at once, and guests see it right away.',
      saving: 'Saving...',
      coverLabel: 'Cover',
      coverNone: 'No cover yet',
      coverUploaded: 'Uploaded cover',
      coverPicked: 'A photo from the gallery',
      coverPreparing: 'Preparing the cover...',
      coverUpload: 'Upload JPG or PNG',
      coverUploading: 'Uploading...',
      coverUploadedDone: 'Cover uploaded',
      coverPickLabel: 'Or pick a photo from the gallery',
      coverPickItem: 'Make {name} the cover',
      coverEmpty: 'Upload photos to pick a cover from them.',
      coverHint: 'An uploaded cover is not a gallery file: it is not in downloads and never carries the Watermark.',
      coverErrors: {
        fileType: 'Only a JPG or PNG photo can be the cover.',
        tooLarge: 'The cover can be up to 50 MB.',
        STORAGE_LIMIT_EXCEEDED: 'There is not enough space left in your storage.',
        FAILED: 'Could not upload the cover. Please try again.',
      },
      styleLabel: 'Page style',
      layoutLabel: 'Photo layout',
      lookHint: 'Fonts and colours come from your site’s design.',
      spacingLabel: 'Photo spacing',
      spacings: {
        DESIGN: 'As in the design',
        SMALLER: 'Smaller',
        LARGER: 'Larger',
        NONE: 'None',
      },
      shapeLabel: 'Photo shape',
      shapes: {
        SQUARE: 'Square',
        ORIGINAL: 'Original',
        PORTRAIT: 'Vertical 4:5',
        LANDSCAPE: 'Horizontal 3:2',
      },
      watermarkProgress: 'Watermark: {ready}/{total} ready',
      watermarkFailed: 'Could not be made: {count}',
      previewLabel: 'Preview',
      previewFrameTitle: 'The gallery as guests see it',
      previewHint: 'The preview shows the saved gallery and reloads after each change.',
      desktop: 'Desktop',
      phone: 'Phone',
    },
    plan: {
      badge: 'Pro',
      lockedHint: 'Included from the Pro plan. Pro is bought in the Matcha app, under My subscription.',
    },
    cancel: 'Cancel',
  },
  reviewsPage: {
    navLabel: 'Reviews',
    title: 'Reviews',
    tabReports: 'Reports · {count}',
    tabAll: 'All reviews',
    tabHidden: 'Hidden',
    starsLabel: 'Rating',
    starsAll: 'All ratings',
    starsOption: '{stars} ★',
    profileFilter: 'Profile: {profile}',
    clearProfileFilter: 'Clear the profile filter',
    filterByProfile: 'Show this profile’s reviews',
    filtersUnavailableForReports: 'Filters apply to the “All reviews” and “Hidden” tabs.',
    profileFallback: 'Non-public profile',
    columnProfile: 'Profile',
    columnStars: '★',
    columnReview: 'Review and reply',
    columnChannel: 'Channel',
    columnReport: 'Report',
    columnActions: 'Actions',
    replyLabel: 'Reply: {reply}',
    replyNone: 'none',
    channelLink: 'Review link',
    channelEmail: 'Email code',
    reportOpen: 'Open since {date}',
    reportRejected: 'Rejected {date}',
    reportUpheld: 'Upheld {date}',
    reportNone: '—',
    reportMessage: 'Provider’s note: {message}',
    hiddenStatus: 'Hidden: {reason}',
    hideAction: 'Hide',
    rejectReportAction: 'Reject report',
    hideDialogTitle: 'Hide the review by {author}?',
    hideDialogDescription: 'The review disappears from every page and from the rating. The provider will see the reason.',
    reasonLabel: 'Reason',
    reasonSpam: 'Spam',
    reasonAbuse: 'Abuse',
    reasonFake: 'Fake',
    reasonNotAboutProvider: 'Not about this provider',
    cancel: 'Cancel',
    loading: 'Loading reviews...',
    loadFailed: 'Unable to load reviews. Please try again.',
    emptyReports: 'No open reports.',
    emptyReviews: 'No reviews found.',
    forbidden: 'Only Matcha moderators can open this section.',
    hidden: 'Review hidden.',
    reportRejectedSuccess: 'Report rejected.',
    hideFailed: 'Unable to hide the review. Please try again.',
    rejectFailed: 'Unable to reject the report. Please try again.',
    loadMore: 'Show more',
  },
  chatPage: {
    defaultGreeting:
      'Hey, I\'m {assistantName} - an AI assistant that helps you plan events and find service providers. I can help you find a photographer, an event host, or another provider, as well as specific services. Tell me what you need, for example: "Need a photographer for a wedding on Friday at 6pm in {city}, budget up to $500."',
    dynamicCapabilitiesIntro: 'Based on my currently available tools, I can help with',
    dynamicCapabilitiesFallback:
      'I can still help you plan in plain language and guide the next steps.',
    dynamicCapabilityEvents: 'event planning and updates',
    dynamicCapabilityPositions: 'drafting and managing positions',
    dynamicCapabilityProviders: 'finding providers and services',
    dynamicCapabilitySkills: 'matching required skills',
    dynamicCapabilitySummaries: 'event summaries and status recaps',
    sendError: 'Unable to send message.',
    fallbackAssistantReply: 'I could not complete that request. Please rephrase and try again.',
    authRequiredHeading: 'Authentication required',
    authRequiredDescription: 'Sign in to access chat.',
    authRequiredBanner: 'Chat is available only to authenticated users.',
    authAction: 'Sign in / Sign up',
    heading: 'Chat',
    subtitle: 'Chat with {assistantName} to plan events in plain language.',
    you: 'You',
    thinking: 'Thinking...',
    messageLabel: 'Message',
    messagePlaceholder: 'Message {assistantName}...',
    composerHint: 'Enter to send, Shift+Enter for a new line.',
    send: 'Send',
  },
  serviceDetailsPage: {
    missingTitle: 'Service not found',
    titleSuffix: 'Matcha Events Services',
    priceModeFrom: 'From pricing',
    priceModeRange: 'Range pricing',
    priceModeFixed: 'Fixed pricing',
    backToServices: 'Back to services',
    viewProfile: 'View profile',
    aboutProvider: 'About this provider',
    providerDescriptionFallback: 'This provider has not added a profile description yet.',
    moreServices: 'More services from {displayName}',
    browseAllServices: 'Browse all services',
  },
  profileDetailsPage: {
    storageUsageTemplate: 'Storage: {used} / {limit} GB',
    storageUsageUnlimitedTemplate: 'Storage: {used} GB',
    missingTitle: 'Profile not found',
    backToProfiles: 'Back to profiles',
    servicesLabel: 'Services',
    typicalBudgetRange: 'Budget range:',
    locations: 'Locations:',
    languages: 'Languages:',
    latestServices: 'Check my latest services',
    showAllServices: 'Show all {fullName} services',
    noServices: 'This profile has no active services yet.',
    viewService: 'View service',
    openService: 'Open service',
    moreServicesAvailable: 'more services available',
    contactPhone: 'Phone',
    contactEmail: 'Email',
    contactOther: 'Other',
    socialTwitter: 'Twitter/X',
    socialInstagram: 'Instagram',
    socialThreads: 'Threads',
    socialFacebook: 'Facebook',
    socialTikTok: 'TikTok',
    socialYouTube: 'YouTube',
    socialWebsite: 'Website',
    socialOther: 'Other',
  },
  profileServicesPage: {
    missingTitle: 'Profile services not found',
    title: '{displayName} services',
    description: 'Browse all active services offered by {displayName} on Matcha Events.',
    heading: '{displayName} services',
    backToProfile: 'Back to profile',
    noServices: 'This profile has no active services yet.',
  },
  positionDetailsPage: {
    missingTitle: 'Position not found',
    titleSuffix: 'Matcha Events Positions',
    badge: 'Available position',
  },
  subscriptionPage: {
    navLabel: 'Subscription',
    title: 'My subscription',
    loading: 'Loading your subscription...',
    loadFailed: 'Could not load your subscription. Please try again.',
    current: 'Your subscription',
    names: { free: 'Free', pro: 'Pro' },
    compare: {
      storage: { label: 'Storage', free: '1 GB', pro: '5 GB' },
      galleries: { label: 'Client galleries', free: 'up to 3', pro: 'unlimited' },
      gallerySettings: { label: 'Watermark, access end date, selection limit', free: '—', pro: '✓' },
      webDownloads: { label: 'Web-size downloads', free: '—', pro: '✓' },
      matchaMark: { label: 'Galleries without the Matcha mark', free: '—', pro: '✓' },
      ownAssistant: { label: 'Your own AI assistant for the site', free: '—', pro: '✓' },
    },
    getProTitle: 'Pro is bought in the Matcha app',
    getProBody: 'Open My subscription in the app: the first 7 days are free, then it is monthly. Once bought, Pro works here too.',
    appStore: 'App Store',
    googlePlay: 'Google Play',
    status: {
      trial: 'Free week until {{date}}',
      renews: 'Renews on {{date}}',
      ends: 'Cancelled. Pro lasts until {{date}}',
    },
    manage: {
      APP_STORE: 'Bought in the App Store. Manage or cancel it on an iPhone: Settings → your name → Subscriptions.',
      PLAY_STORE: 'Bought in Google Play. Manage or cancel it in Google Play → Payments & subscriptions.',
      INTERNAL: 'Pro was given by the Matcha team.',
    },
  },
  sitePage: {
    navLabel: 'My site',
    title: 'My site',
    status: {
      create: 'Create',
      draft: 'Draft',
      published: 'Published',
      disabled: 'Turned off',
    },
    publishedVersion: 'Published · version {version}',
    notPublished: 'Not published yet',
    disabledStatus: 'Turned off: visitors cannot see the site',
    open: 'Open',
    share: 'Share',
    draft: {
      title: 'Unpublished changes',
      message: 'Visitors see the previous version.',
      firstMessage: 'Look at the draft and publish your site.',
      preview: 'Preview',
      publish: 'Publish',
      published: 'Your site is published',
      publishedMessage: 'Visitors already see the new version.',
      discard: 'Discard changes',
      discarded: 'Changes discarded',
      discardSheet: {
        title: 'Discard changes?',
        message: 'The draft will be deleted; the site stays as published.',
        confirm: 'Discard changes',
        keep: 'Keep',
      },
    },
    changeDesign: 'Change the design',
    remainingEdits: 'Edits left today: {count}',
    remainingBuilds: 'Site builds left today: {count}',
    versions: {
      title: 'Versions',
      version: 'Version {number}',
      current: 'live',
      restore: 'Restore',
      hint: 'Restore puts the version into the draft. It goes live when you publish it.',
      restored: 'Version {number} is back in the draft',
      restoredMessage: 'Look at the draft and publish it when you are ready.',
    },
    language: {
      title: 'Site language',
      hint: 'The language of the site’s buttons, sections and labels. Your own texts from the Profile stay as they are.',
      options: {
        uk: 'Українська',
        en: 'English',
        ru: 'Русский',
      },
    },
    disable: 'Turn off the site',
    enable: 'Turn on the site',
    disableConfirmTitle: 'Turn off the site?',
    disableConfirmMessage: 'Visitors will no longer see it. You can turn it back on any time; the versions stay.',
    empty: {
      heading: 'Your own site, made from your profile',
      body: 'Describe the site you have in mind and the AI designer makes it. Texts, prices, photos and contacts come from your profile, so the site updates itself when you change the profile.',
      examplesTitle: 'For example',
      examples: {
        example1: 'Dark and stylish, like a night club, with neon accents',
        example2: 'Light and gentle, for weddings, lots of air and photos',
        example3: 'Like a site I like (link), but in my colours',
        example4: 'Bright and bold: big letters, lots of colour',
        example5: 'Minimal and strict, like a design studio',
        example6: 'Warm and cosy, earthy colours, a handmade feel',
        example7: 'Like a glossy magazine: big photos and beautiful headlines',
        example8: '1970s retro, warm colours and rounded fonts',
        example9: 'Modern and techy: dark background, clean lines',
        example10: 'Romantic, pastel colours, soft shapes',
        example11: 'Premium: lots of space, thin lines, a restrained palette',
        example12: 'Playful and fun, for kids’ parties and celebrations',
      },
      inputLabel: 'What should the site be like?',
      placeholder: 'Style, mood, colours. You can add a link to a site you like.',
      create: 'Create the site',
      hint: 'Takes 2–3 minutes. We will send a notification when it is ready.',
    },
    assistant: {
      title: 'Edit your site in your own Claude',
      body: 'If you have Claude, it can change this site’s design. These edits do not use your daily limit.',
      connect: 'Connect Claude',
      connected: '{client} connected',
      lastUsed: 'Last edit {date}',
      neverUsed: 'No edits yet',
      disconnect: 'Disconnect',
      disconnectConfirmTitle: 'Disconnect {client}?',
      disconnectConfirmMessage: '{client} will no longer be able to change your site. You can connect it again any time.',
      disconnected: '{client} disconnected',
      disconnectClient: 'Disconnect {client}',
      changeTitle: 'Change the design in your own Claude',
      changeBody: 'Connect the Claude you already use: it will change your site’s colors, fonts and sections. The facts on your site come from your profile.',
      locked: {
        title: 'Your own AI assistant',
        body: 'Change your site by talking to your own Claude or ChatGPT. Available in the Pro plan.',
      },
      createWithClaude: 'Create a site with Claude',
      changeWithClaude: 'Change the design with Claude',
      prompt: {
        create: 'Create a website for me on Matcha. Look at my profile and suggest a design.',
        change: 'Change the design of my website on Matcha ({site}). First look at how it is now and ask me what to change.',
      },
      create: {
        title: 'Create your site in your own Claude',
        body: 'Connect the Claude you already use, and it will build a site from your profile: your services, prices, photos and contacts come in on their own.',
        steps: {
          connect: 'Connect Claude and confirm here',
          create: 'Click “Create a site with Claude” and send the ready request',
          publish: 'Claude shows you a preview and publishes when you say so',
        },
        note: 'You need your own Claude (claude.ai). Matcha does not pay for it and charges you nothing.',
      },
      request: {
        title: 'Allow {client} to edit your site?',
        can: 'Change the draft’s design and publish it when you ask',
        cannot: 'Cannot change your profile, services, prices or photos, or turn the site off',
        note: 'The request comes from your “Connect Claude” link. If you did not make it, decline.',
        allow: 'Allow',
        deny: 'Decline',
        allowedTitle: '{client} connected',
        allowedMessage: 'Go back to {client} and ask it to change your site. You can disconnect it on the My site page.',
        deniedTitle: 'Declined',
        deniedMessage: '{client} got no access to your site.',
        done: 'Done',
        close: 'Close',
        none: 'This request has already ended.',
      },
    },
    chat: {
      title: 'Site designer',
      subtitle: 'Changes the design only',
      intro: 'Write what to change on the site: colours, fonts, sections, mood. You can add a link to an example site.',
      working: 'The designer is working…',
      steps: {
        reading: 'Looking at the site and profile',
        designing: 'Changing the design',
        checking: 'Checking the pages',
      },
      closeHint: 'You can close this page: we will send a notification to the app.',
      previewDraft: 'Preview the draft',
      placeholder: 'What should change on the site?',
      waitingPlaceholder: 'Wait until the designer finishes',
      inputLabel: 'Message to the designer',
      send: 'Send',
      limitReached: 'Today’s limit is used up. New tries arrive at midnight Kyiv time.',
    },
    designs: {
      section: 'Design',
      title: 'Ready designs',
      startBody: 'Choose a ready design, and your site will be ready in a minute. Texts, prices, photos and contacts come from your profile, so the site updates itself whenever you change your profile.',
      choose: 'Choose a ready design',
      startCount: {
        one: '{count} design. Those for your categories come first.',
        few: '{count} designs. Those for your categories come first.',
        many: '{count} designs. Those for your categories come first.',
        other: '{count} designs. Those for your categories come first.',
      },
      aiTitle: 'Describe your site to the AI designer',
      aiBody: 'Tell it about the style, mood and colors, and it will make a design in 2–3 minutes.',
      aiAction: 'Describe your site',
      rowCurrent: 'Current: {name} · {count} in total',
      rowCount: {
        one: '{count} design to choose from',
        few: '{count} designs to choose from',
        many: '{count} designs to choose from',
        other: '{count} designs to choose from',
      },
      versionSummary: 'Ready design “{name}”',
      all: 'All',
      seeAll: 'All {count} ›',
      hint: 'Click a design to see it with your own data.',
      otherGroups: 'Other groups',
      current: 'Current',
      viewLabel: 'Show the design on',
      viewDesktop: 'Desktop',
      viewMobile: 'Phone',
      groupsLabel: 'Design groups',
      tileLabel: 'Design {name}',
      groups: {
        universal: 'Universal',
        'photo-video': 'Photo & video',
        'hosts-shows': 'Hosts & shows',
        music: 'Music',
        'decor-floristry': 'Decor & floristry',
        'food-sweets': 'Food & sweets',
        venues: 'Venues',
        'wedding-fashion': 'Wedding fashion',
        beauty: 'Beauty',
        'tech-effects': 'Equipment & effects',
        'ceremony-planning': 'Ceremony & planning',
        transport: 'Transport',
        printing: 'Printing',
      },
      universalDesign: 'Universal design',
      tryOnMyData: 'Preview with my data',
      apply: 'Apply',
      applyHint: 'The design will replace the draft. Visitors will see it once you publish the site.',
      replace: {
        title: 'Replace the draft?',
        message: 'The draft has unpublished changes. The “{name}” design will replace them.',
        versionsKept: 'Published versions stay, and you can restore any of them.',
        confirm: 'Replace the draft',
      },
      applied: 'The “{name}” design is in the draft',
      loadFailed: 'Could not load the designs',
      retry: 'Try again',
      errors: {
        previewFailedTitle: 'Could not open the preview',
        applyFailedTitle: 'Could not apply the design',
      },
    },
    errors: {
      loadFailedTitle: 'Could not load the site',
      sendFailedTitle: 'Could not send the message',
      publishFailedTitle: 'Could not publish the site',
      restoreFailedTitle: 'Could not restore the version',
      discardFailedTitle: 'Could not discard the changes',
      disableFailedTitle: 'Could not change the site',
      languageFailedTitle: 'Could not change the site language',
      tryAgain: 'Please try again.',
    },
    linkCopied: 'Link copied',
    profileMissing: 'No profile found. Create a profile first.',
    back: 'Back',
    cancel: 'Cancel',
    or: 'or',
  },
};
